import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { api, formatApiError } from "../../lib/api";
import { useT } from "../../lib/i18n";
import { toast } from "sonner";

export default function AlarmFormDialog({ open, onOpenChange, chart, alarm, onSaved }) {
  const editing = Boolean(alarm);
  const t = useT();
  const [form, setForm] = useState(initial());
  const [saving, setSaving] = useState(false);

  function initial() {
    return {
      name: t("alarm.threshold_alert"),
      condition: "gt",
      threshold: 100,
      interval_minutes: 5,
      enabled: true,
      recipients: "",
    };
  }

  useEffect(() => {
    if (!open) return;
    setForm(
      alarm
        ? {
            name: alarm.name,
            condition: alarm.condition,
            threshold: alarm.threshold,
            interval_minutes: alarm.interval_minutes,
            enabled: alarm.enabled,
            recipients: (alarm.recipient_emails || []).join(", "),
          }
        : initial()
    );
  }, [open, alarm]);

  function update(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function submit() {
    setSaving(true);
    const recipient_emails = form.recipients
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    const payload = {
      name: form.name,
      condition: form.condition,
      threshold: Number(form.threshold),
      interval_minutes: Number(form.interval_minutes),
      enabled: form.enabled,
      recipient_emails,
    };
    try {
      if (editing) {
        await api.patch(`/alarms/${alarm.id}`, payload);
        toast.success(t("common.save"));
      } else {
        await api.post("/alarms", { ...payload, chart_id: chart.id });
        toast.success(t("alarm.create_alarm"));
      }
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="alarm-form-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? t("alarm.edit") : t("alarm.new")}</DialogTitle>
          <DialogDescription>
            {chart ? <>{t("chart.title")}: <span className="font-medium">{chart.title}</span></> : null}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="al-name">{t("common.name")}</Label>
            <Input id="al-name" value={form.name} onChange={(e) => update("name", e.target.value)} data-testid="alarm-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>{t("alarm.condition")}</Label>
              <Select value={form.condition} onValueChange={(v) => update("condition", v)}>
                <SelectTrigger data-testid="alarm-condition"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="gt">{t("alarm.gt")}</SelectItem>
                  <SelectItem value="lt">{t("alarm.lt")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="al-th">{t("alarm.threshold")}</Label>
              <Input
                id="al-th"
                type="number"
                value={form.threshold}
                onChange={(e) => update("threshold", e.target.value)}
                data-testid="alarm-threshold"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>{t("alarm.interval")}</Label>
            <Select value={String(form.interval_minutes)} onValueChange={(v) => update("interval_minutes", Number(v))}>
              <SelectTrigger data-testid="alarm-interval"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="5">{t("alarm.every_n_min", { n: 5 })}</SelectItem>
                <SelectItem value="10">{t("alarm.every_n_min", { n: 10 })}</SelectItem>
                <SelectItem value="30">{t("alarm.every_n_min", { n: 30 })}</SelectItem>
                <SelectItem value="60">{t("alarm.every_n_min", { n: 60 })}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="al-rec">{t("alarm.recipients")}</Label>
            <Input
              id="al-rec"
              value={form.recipients}
              onChange={(e) => update("recipients", e.target.value)}
              placeholder="alice@acme.com, ops@acme.com"
              data-testid="alarm-recipients"
            />
          </div>
          <div className="flex items-center justify-between">
            <Label>{t("alarm.enabled")}</Label>
            <Switch checked={form.enabled} onCheckedChange={(v) => update("enabled", v)} data-testid="alarm-enabled" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("common.cancel")}</Button>
          <Button onClick={submit} disabled={saving} data-testid="alarm-save">
            {saving ? t("common.loading") : editing ? t("common.saveChanges") : t("alarm.create_alarm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
